import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, MapPin, Rotate3d, Loader2, X, Smartphone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import PanoramaViewer from "@/components/PanoramaViewer";
import { api, formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { CASE_STUDIES } from "@/lib/data";

export default function ProjectDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const project = CASE_STUDIES[slug];
  const [pano, setPano] = useState(null); // null | "loading" | dataURL
  const [showPano, setShowPano] = useState(false);

  const openWalkthrough = async () => {
    setShowPano(true);
    trackEvent("view_360_open", {
      event_category: "project",
      project_slug: slug,
      project_title: project?.title,
    });
    if (pano && pano !== "loading") return;
    setPano("loading");
    try {
      const { data } = await api.post(`/projects/${slug}/panorama`, {
        room: project.category,
        style: project.meta.Style,
        notes: project.title,
      });
      setPano(data.panorama);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load the 360 view");
      setPano(null);
      setShowPano(false);
    }
  };

  if (!project) {
    return (
      <main data-testid="project-not-found" className="pt-40 pb-28 relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 text-center">
        <h1 className="font-serif text-5xl text-[#143D63] mb-6">Project not found</h1>
        <Link to="/gallery" className="text-[#8B0000] font-medium link-underline">Back to gallery</Link>
      </main>
    );
  }

  return (
    <main data-testid="project-detail" className="relative z-10">
      <Seo
        title={`${project.title} · ${project.location.split(",")[0]} — ZEBO`}
        description={project.intro}
        path={`/projects/${slug}`}
        image={project.cover}
      />
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[460px] overflow-hidden bg-[#143D63]">
        <img src={project.cover} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 via-[#0A0A0A]/20 to-transparent" />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col justify-end pb-14">
          <button
            data-testid="project-back-btn"
            onClick={() => nav("/gallery")}
            className="inline-flex items-center gap-2 text-[#F9F8F6]/80 text-sm mb-6 hover:text-[#F8C21C] transition-colors w-fit"
          >
            <ArrowLeft size={16} /> All projects
          </button>
          <p className="overline text-[#F8C21C] mb-3">{project.category}</p>
          <h1 className="font-serif text-5xl sm:text-7xl font-light text-[#F9F8F6] tracking-tight leading-[0.95]">{project.title}</h1>
          <p className="flex items-center gap-2 text-[#F9F8F6]/80 mt-4"><MapPin size={16} /> {project.location}</p>
          <p className="text-[#F9F8F6]/50 text-xs mt-3" data-testid="concept-imagery-note">Representative concept imagery — scope, location and timeline from a delivered ZEBO project.</p>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-7">
          <p className="font-serif text-3xl sm:text-4xl font-light text-[#143D63] leading-snug mb-8">{project.intro}</p>
          {project.body.map((p, i) => (
            <p key={i} className="text-[#143D63]/75 leading-relaxed mb-5">{p}</p>
          ))}
        </Reveal>

        <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.1}>
          <div className="border border-[#143D63]/15 divide-y divide-[#143D63]/10">
            {Object.entries(project.meta).map(([k, v]) => (
              <div key={k} className="flex justify-between px-5 py-4">
                <span className="text-sm text-[#143D63]/60">{k}</span>
                <span className="text-sm font-semibold text-[#143D63]">{v}</span>
              </div>
            ))}
          </div>
          <Link
            to="/consultation"
            data-testid="project-book-btn"
            className="group mt-6 inline-flex items-center gap-2 bg-[#F8C21C] text-[#143D63] px-7 py-4 font-bold text-sm w-full justify-center hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors duration-300"
          >
            Get a design like this
            <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
          <button
            data-testid="project-360-btn"
            onClick={openWalkthrough}
            className="mt-3 inline-flex items-center gap-2 border border-[#143D63] text-[#143D63] px-7 py-4 font-bold text-sm w-full justify-center hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors duration-300"
          >
            <Rotate3d size={16} /> Enter 360° Walkthrough
          </button>
          {(project.category || "").toLowerCase().includes("kitchen") && (
            <Link
              to={`/configurator?style=${encodeURIComponent(project.meta?.Style || "")}&preset=${encodeURIComponent(slug)}`}
              data-testid="project-ar-btn"
              className="mt-3 inline-flex items-center gap-2 border border-[#F8C21C] text-[#F8C21C] bg-[#143D63] px-7 py-4 font-bold text-sm w-full justify-center hover:bg-[#F8C21C] hover:text-[#143D63] transition-colors duration-300"
              onClick={() => trackEvent("project_ar_open", { event_category: "project", project_slug: slug })}
            >
              <Smartphone size={16} /> View this style in AR
            </Link>
          )}
        </Reveal>
      </section>

      {/* 360 Walkthrough modal */}
      <AnimatePresence>
        {showPano && (
          <motion.div
            data-testid="project-360-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowPano(false)}
            className="fixed inset-0 z-[60] bg-[#0A0A0A]/90 flex items-center justify-center p-4"
          >
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl">
              <div className="flex items-center justify-between mb-3 text-[#F9F8F6]">
                <p className="font-serif text-xl flex items-center gap-2"><Rotate3d size={18} className="text-[#F8C21C]" /> {project.title} — 360° view</p>
                <button data-testid="project-360-close" onClick={() => setShowPano(false)} className="hover:text-[#F8C21C]"><X size={24} /></button>
              </div>
              <div className="bg-[#143D63] aspect-[16/9] w-full overflow-hidden">
                {pano === "loading" ? (
                  <div className="w-full h-full flex flex-col items-center justify-center text-center px-8">
                    <Loader2 className="animate-spin text-[#F8C21C] mb-4" size={38} />
                    <p className="font-serif text-2xl text-[#F9F8F6] font-light">Building your 360° walkthrough…</p>
                    <p className="text-[#F9F8F6]/60 text-sm mt-2">First view takes ~20–30s, then it loads instantly.</p>
                  </div>
                ) : pano ? (
                  <PanoramaViewer src={pano} />
                ) : null}
              </div>
              <p className="text-[#F9F8F6]/60 text-xs mt-2 text-center">Drag to look around · scroll to zoom · fullscreen available</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image gallery */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-24">
        <div className="grid md:grid-cols-3 gap-4">
          {project.gallery.map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className={`overflow-hidden ${i === 0 ? "md:col-span-3 aspect-[16/8]" : "aspect-[4/3]"}`}
            >
              <img src={img} alt={`${project.title} view ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </motion.div>
          ))}
        </div>
      </section>
    </main>
  );
}
